import * as React from 'react';
import { theme } from '@Guidelines/Theme/theme';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { css } from '@emotion/react';
import useSettingsPanel from '@Guidelines/Shell/AppShell/Hooks/useSettingsPanel';
import AiCostLimitBanner from '@Guidelines/Shell/AiCostLimitBanner';
import AppBreadcrumb from '@Guidelines/Shell/AppShell/BreadCrumbs/AppBreadcrumb';
import NavigationHomeBreadcrumb from '@Guidelines/Shell/AppShell/BreadCrumbs/NavigationHomeBreadcrumb';
import { Outlet } from 'react-router-dom';
import { Message } from '@verclam/ui-kit';

/**
 * Takes whatever width the navigation column leaves, and the height `AppShell`'s row leaves below the
 * header, rather than subtracting either from the viewport.
 *
 * `min-height: 0` is what makes the height work. A flex item refuses by default to shrink below its
 * content, so without it a long page would stretch this box past the row and every internally scrolling
 * screen below would scroll the document instead. `min-width: 0` does the same for the width.
 */
const mainStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	flex: 1;
	min-width: 0;
	min-height: 0;
	max-width: ${theme.layout.maxWidth};
	margin: 0 auto;
	padding: 2rem ${theme.layout.gutter};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		padding: 1.25rem 1rem;
	}
`;

/**
 * Beside a pinned panel the page stays on the top bar's centered axis when the row has room for it:
 * the left margin is the gap between the panel's right edge and the centered column's left edge
 * (`(row - maxWidth) / 2 - panel`), and never negative, so the page never starts under the panel.
 */
const pinnedMainStyle = css`
	margin-left: max(0px, calc((100% - ${theme.layout.maxWidth}) / 2 - ${theme.layout.settingsPanelWidth}));
`;

/**
 * Breadcrumb + routed page. Reads both `useAuthenticatedUser` and `useCurrentOrganization`
 * directly (no prop drilling from `AppShell`, which mounts the providers this depends on but sits
 * above them).
 *
 * Three distinct "nothing to show" states, never conflated: `user.account === null` is a support
 * account, attached to no account at all (checked first, since it also leaves `currentOrganization`
 * null); `startupError !== null` is a failed organizations or facilities fetch — including a `403` on a
 * custom role missing `ORGANIZATION_READ` or `FACILITY_READ`, which gets its own scoped message rather
 * than the generic one — checked next, since it also leaves `currentOrganization` null without the
 * caller actually having zero organizations; `hasConfirmedNoOrganizations` is a regular user whose every
 * organization assignment has been withdrawn — reachable mid-session, since `users_organizations`
 * cascades on delete. Every scoped page below assumes a resolved organization, so this is the one place
 * that must stop `<Outlet>` from ever mounting without one.
 */
const AppMain: React.FC = () => {
	const { user } = useStrictAuthenticatedUser();
	const { mode } = useSettingsPanel();
	const { startupError, hasConfirmedNoOrganizations } = useCurrentOrganization();

	const messages = React.useMemo<{
		severity: React.ComponentProps<typeof Message>['severity'];
		text: string;
	} | null>(() => {
		if (user.account === null) {
			return {
				severity: 'warn',
				text: "Vous n'êtes rattaché à aucune structure. Contactez votre administrateur.",
			};
		}

		if (startupError !== null) {
			if (startupError.status === 403 && startupError.scope === 'organizations') {
				return {
					severity: 'error',
					text: "Votre rôle ne permet pas d'afficher vos structures. Contactez votre administrateur.",
				};
			}

			if (startupError.status === 403 && startupError.scope === 'facilities') {
				return {
					severity: 'error',
					text: "Votre rôle ne permet pas d'afficher vos agences. Contactez votre administrateur.",
				};
			}

			return {
				severity: 'error',
				text: "Vos structures et agences n'ont pas pu être chargées.",
			};
		}

		if (hasConfirmedNoOrganizations) {
			return {
				severity: 'warn',
				text: "Vous n'êtes affecté à aucune structure. Contactez votre administrateur.",
			};
		}

		return null;
	}, [user, startupError, hasConfirmedNoOrganizations]);

	return (
		<main css={[mainStyle, mode === 'pinned' ? pinnedMainStyle : null]}>
			{messages ? (
				<Message severity={messages.severity} text={messages.text} />
			) : (
				<React.Fragment>
					<AiCostLimitBanner />
					<NavigationHomeBreadcrumb />
					<AppBreadcrumb />
					<Outlet />
				</React.Fragment>
			)}
		</main>
	);
};

export default AppMain;
